import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { supabase, isSupabaseConfigured, isBypassAuthEnabled } from '@/lib/supabase';
import { trackSignup, trackLogin, identifyUser, resetUser } from '@/lib/posthog';
import { stashReturnTo, clearReturnTo } from '@/lib/authReturnTo';
import type { Session, User } from '@supabase/supabase-js';

/**
 * Distinguishes a user-initiated sign-out from a session expiring underneath
 * the user (AUTH-002). Supabase fires the same SIGNED_OUT event for both; only
 * the involuntary path should stash a return route for post-login recovery.
 */
let deliberateSignOut = false;

export type UserRole = 'owner' | 'pilot' | 'inspector' | 'admin';

export interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  role: UserRole;
  organization_id: string | null;
  language: string;
  created_at: string;
}

interface AuthState {
  session: Session | null;
  user: User | null;
  profile: UserProfile | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  initialize: () => Promise<void>;
  requestEmailOtp: (email: string) => Promise<void>;
  verifyEmailOtp: (email: string, token: string) => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, role: UserRole, fullName: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  skipAuth: () => void;
  signOut: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      session: null,
      user: null,
      profile: null,
      isLoading: true,
      isInitialized: false,
      error: null,

      initialize: async () => {
        try {
          set({ isLoading: true });

          // BYPASS_AUTH mode for local development & demos
          if (isBypassAuthEnabled) {
            // eslint-disable-next-line no-console
            console.info('[Auth] BYPASS_AUTH enabled — using demo user');
            const demoProfile: UserProfile = {
              id: 'demo-user-bypass',
              email: 'demo@beetlesense.local',
              full_name: 'Demo User (Bypass)',
              avatar_url: null,
              role: 'owner',
              organization_id: null,
              language: 'en',
              created_at: new Date().toISOString(),
            };
            set({
              session: {} as Session,
              user: { id: 'demo-user-bypass', email: 'demo@beetlesense.local' } as User,
              profile: demoProfile,
              isLoading: false,
              isInitialized: true,
            });
            return;
          }

          if (!isSupabaseConfigured) {
            set({
              session: null,
              user: null,
              profile: null,
              isLoading: false,
              isInitialized: true,
            });
            return;
          }

          // Timeout: if Supabase doesn't respond in 8s, proceed without auth
          const sessionPromise = supabase.auth.getSession();
          const timeoutPromise = new Promise<{ data: { session: null } }>((resolve) =>
            setTimeout(() => resolve({ data: { session: null } }), 8000),
          );
          const {
            data: { session },
          } = await Promise.race([sessionPromise, timeoutPromise]);

          if (session?.user) {
            const profile = await fetchProfile(session.user.id);
            set({
              session,
              user: session.user,
              profile,
              isLoading: false,
              isInitialized: true,
            });
          } else {
            set({
              session: null,
              user: null,
              profile: null,
              isLoading: false,
              isInitialized: true,
            });
          }

          // Listen for auth state changes (including session expiry)
          const {
            data: { subscription },
          } = supabase.auth.onAuthStateChange(async (event, newSession) => {
            if (event === 'SIGNED_IN' && newSession?.user) {
              deliberateSignOut = false;
              const profile = await fetchProfile(newSession.user.id);
              set({ session: newSession, user: newSession.user, profile, error: null });
            } else if (event === 'SIGNED_OUT') {
              // AUTH-002: session expired (token refresh failed) rather than the
              // user clicking "sign out" — remember where they were so the login
              // screen can promise "dina uppgifter är sparade" and send them back.
              // In-flight form drafts survive via useFormPersistence (localStorage).
              if (!deliberateSignOut && get().session) {
                stashReturnTo(window.location.pathname + window.location.search);
              }
              deliberateSignOut = false;
              set({ session: null, user: null, profile: null, error: null });
            } else if (event === 'TOKEN_REFRESHED' && newSession) {
              set({ session: newSession, error: null });
            }
          });
          // Store unsubscribe for cleanup (called nowhere currently, but available)
          (globalThis as Record<string, unknown>).__beetlesense_auth_unsub = () =>
            subscription.unsubscribe();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Auth initialization failed:', err);
          set({
            session: null,
            user: null,
            profile: null,
            isLoading: false,
            isInitialized: true,
            error: 'Failed to initialize authentication',
          });
        }
      },

      requestEmailOtp: async (email: string) => {
        if (!isSupabaseConfigured) {
          set({
            error:
              'Backend not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.',
          });
          return;
        }
        set({ isLoading: true, error: null });
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            // HQ access is provisioned ahead of time. Never let this sign-in path
            // create arbitrary public accounts.
            shouldCreateUser: false,
          },
        });
        if (error) {
          set({
            isLoading: false,
            error: 'Unable to send a sign-in code. Please wait and try again.',
          });
          throw error;
        }
        set({ isLoading: false });
      },

      verifyEmailOtp: async (email: string, token: string) => {
        if (!isSupabaseConfigured) {
          set({
            error:
              'Backend not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.',
          });
          return;
        }
        set({ isLoading: true, error: null });
        const { data, error } = await supabase.auth.verifyOtp({
          email,
          token,
          type: 'email',
        });
        if (error || !data.session?.user) {
          set({
            isLoading: false,
            error: 'Invalid or expired code. Request a new code and try again.',
          });
          throw error ?? new Error('OTP verification did not create a session');
        }

        const profile = await fetchProfile(data.session.user.id);
        if (!profile) {
          await supabase.auth.signOut();
          set({
            session: null,
            user: null,
            profile: null,
            isLoading: false,
            error: 'Your account is not provisioned for this workspace.',
          });
          throw new Error('OTP user has no provisioned profile');
        }

        set({
          session: data.session,
          user: data.session.user,
          profile,
          isLoading: false,
          error: null,
        });
        identifyUser(data.session.user.id, { role: profile.role });
        trackLogin('email_otp');
      },

      signInWithPassword: async (email: string, password: string) => {
        if (!isSupabaseConfigured) {
          set({
            error:
              'Backend not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.',
          });
          return;
        }
        set({ isLoading: true, error: null });
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
          set({ isLoading: false, error: error.message });
          throw error;
        }
        if (data.session?.user) {
          const profile = await fetchProfile(data.session.user.id);
          set({
            session: data.session,
            user: data.session.user,
            profile,
            isLoading: false,
          });
          // Stitch the anonymous (tool-side) PostHog identity to the real user.
          identifyUser(data.session.user.id, { role: profile?.role ?? 'owner' });
          trackLogin('password');
        }
      },

      signUp: async (email: string, password: string, role: UserRole, fullName: string) => {
        if (!isSupabaseConfigured) {
          set({
            error:
              'Backend not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.',
          });
          return;
        }
        set({ isLoading: true, error: null });
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback`,
            data: { role, full_name: fullName },
          },
        });
        if (error) {
          set({ isLoading: false, error: error.message });
          throw error;
        }
        if (data.session?.user) {
          const profile = await fetchProfile(data.session.user.id);
          set({
            session: data.session,
            user: data.session.user,
            profile,
            isLoading: false,
          });
          // Conversion + identity stitch: this anonymous visitor is now a known user.
          identifyUser(data.session.user.id, { role });
          trackSignup('password');
        } else {
          set({ isLoading: false });
          // No session yet (email confirmation required) — still a completed signup request.
          trackSignup('password');
        }
      },

      resetPassword: async (email: string) => {
        if (!isSupabaseConfigured) {
          set({
            error:
              'Backend not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.',
          });
          return;
        }
        set({ isLoading: true, error: null });
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth/callback`,
        });
        if (error) {
          set({ isLoading: false, error: error.message });
          throw error;
        }
        set({ isLoading: false });
      },

      skipAuth: () => {
        // Only allow demo mode in dev or when explicitly enabled via env var
        const demoAllowed = import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEMO === 'true';
        if (!demoAllowed) {
          // eslint-disable-next-line no-console
          console.warn(
            '[Auth] skipAuth() blocked — demo mode is disabled in production. Set VITE_ENABLE_DEMO=true to enable.',
          );
          return;
        }

        const demoProfile: UserProfile = {
          id: 'demo-user-manual',
          email: 'demo@beetlesense.com',
          full_name: 'Demo User',
          avatar_url: null,
          role: 'owner',
          organization_id: null,
          language: 'en',
          created_at: new Date().toISOString(),
        };
        set({
          session: {} as Session,
          user: { id: 'demo-user-manual', email: 'demo@beetlesense.com' } as User,
          profile: demoProfile,
          isLoading: false,
          isInitialized: true,
        });
      },

      signOut: async () => {
        deliberateSignOut = true;
        clearReturnTo();
        set({ isLoading: true });
        const { profile } = get();
        // Demo users: skip Supabase call, just clear local state
        if (profile?.id?.startsWith('demo-user')) {
          set({ session: null, user: null, profile: null, isLoading: false, error: null });
          return;
        }
        try {
          if (isSupabaseConfigured) {
            await supabase.auth.signOut();
          }
        } catch {
          // Sign-out failures shouldn't block state cleanup
        }
        // Clear the PostHog identity so a shared device doesn't attribute the next
        // visitor's events to the user who just signed out.
        resetUser();
        set({ session: null, user: null, profile: null, isLoading: false, error: null });
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'beetlesense-auth-storage',
      partialize: (state) => ({
        session: state.session,
        profile: state.profile,
        user: state.user,
      }),
      // Safe storage adapter: handles private browsing, quota exceeded, corrupted JSON
      storage: {
        getItem: (name: string) => {
          try {
            const raw = localStorage.getItem(name);
            return raw ? JSON.parse(raw) : null;
          } catch {
            // Corrupted JSON or localStorage blocked — start fresh
            try {
              localStorage.removeItem(name);
            } catch {
              /* ignore */
            }
            return null;
          }
        },
        setItem: (name: string, value: unknown) => {
          try {
            localStorage.setItem(name, JSON.stringify(value));
          } catch {
            // QuotaExceededError or private browsing — session won't persist but app still works
          }
        },
        removeItem: (name: string) => {
          try {
            localStorage.removeItem(name);
          } catch {
            /* ignore */
          }
        },
      },
    },
  ),
);

async function fetchProfile(userId: string): Promise<UserProfile | null> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, email, full_name, avatar_url, role, organization_id, language, created_at')
      .eq('id', userId)
      .single();

    if (error) {
      // eslint-disable-next-line no-console
      console.warn('Profile fetch failed:', error.message);
      // Set error in store so UI can show feedback
      useAuthStore.getState().clearError();
      useAuthStore.setState({ error: `Profile could not be loaded: ${error.message}` });
      return null;
    }
    return data as UserProfile;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Profile fetch exception:', err);
    return null;
  }
}
